// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// An order is the only thing a player ever gives, and the only thing that crosses the wire: for each
// unit, up to three waypoints, one of sixteen directions to watch, and a stance. Five small integers
// per unit, so a whole squad's plan is a dozen bytes.
//
// THE WATCH IS QUANTISED IN THE UI, NOT HERE. The thumb drags an arrowhead, the arrowhead snaps to
// one of sixteen sectors, and the sector is what is stored, sent, resolved and drawn. There is never
// an unquantised angle anywhere in the game, which removes a whole class of "it looked like it was
// pointing at the door" and a whole class of cross-device disagreement at once.
//
// ── VALIDATION IS A CLAMP, NEVER A THROW ────────────────────────────────────────────────────────
// These integers arrive from another device. A path through a wall, a waypoint off the board, a
// stance of 97, an array of the wrong length — every one has to produce a LEGAL order rather than an
// exception, because the alternative is one peer resolving a turn the other refused to, and a
// resolver that can throw will throw, on somebody's phone, on a seed nobody tested. `sanitize()` is
// therefore total: it truncates a path at the last reachable waypoint and clamps everything else.
//
// ── THE COMMITMENT ──────────────────────────────────────────────────────────────────────────────
// Both sides plan the same five seconds at the same moment, so orders cannot simply be sent when
// they are ready — the opponent's client would be holding your plan while they were still writing
// theirs. A turn is therefore two messages: a HASH first, then the orders and the nonce. Nobody
// reveals until both commitments are in, and a reveal that does not match its commitment is
// rejected. This is not cryptography against a determined attacker and is not claimed to be: it is
// what stops the obvious peek, in a few lines of arithmetic.

import { MAX_WAYPOINTS, SECTORS } from './tuning';
import { N, W, blocksFeet, tx, ty, type GameMap } from './map';

export type Stance = 0 | 1 | 2;
export const STALK: Stance = 0;
export const MOVE: Stance = 1;
export const RUSH: Stance = 2;

export interface UnitOrder {
  /** Tiles to walk through, in order. Empty means "hold where you are". */
  path: number[];
  /** One of sixteen sectors, or -1 for "watch where you are going". */
  watch: number;
  stance: Stance;
}

export type SideOrders = UnitOrder[];

/** Ints per unit on the wire: [count, w0, w1, w2, watch+1, stance]. */
const SLOTS = 3 + MAX_WAYPOINTS;

export const holdOrder = (): UnitOrder => ({ path: [], watch: -1, stance: STALK });
/** What an absent seat plays, and what a fresh turn starts as. */
export const holdAll = (units: number): SideOrders => Array.from({ length: units }, holdOrder);

export function encode(orders: SideOrders): number[] {
  const out: number[] = [];
  for (const o of orders) {
    const path = o.path.slice(0, MAX_WAYPOINTS);
    out.push(path.length);
    for (let i = 0; i < MAX_WAYPOINTS; i++) out.push(path[i] ?? 0);
    out.push(o.watch + 1);
    out.push(o.stance);
  }
  return out;
}

export function decode(flat: number[] | undefined, units: number): SideOrders {
  const raw = flat ?? [];
  const out: SideOrders = [];
  for (let u = 0; u < units; u++) {
    const base = u * SLOTS;
    const n = Math.max(0, Math.min(MAX_WAYPOINTS, raw[base] | 0));
    const path: number[] = [];
    for (let i = 0; i < n; i++) {
      const v = raw[base + 1 + i] | 0;
      if (v >= 0 && v < N) path.push(v);
    }
    const watch = (raw[base + 1 + MAX_WAYPOINTS] | 0) - 1;
    const stance = raw[base + 2 + MAX_WAYPOINTS] | 0;
    out.push({
      path,
      watch: watch >= 0 && watch < SECTORS ? watch : -1,
      stance: (stance === 1 || stance === 2 ? stance : 0) as Stance,
    });
  }
  return out;
}

/**
 * Can a unit walk the straight line between two tile centres?
 *
 * Sampled at a quarter of a tile, exactly as sight is: a walk that only visits the tiles a
 * mathematical ray crosses lets a path squeeze through the diagonal join of two blocks, which looks
 * completely plausible on screen and is a unit walking through a wall.
 */
export function walkClear(map: GameMap, a: number, b: number): boolean {
  const ax = tx(a) + 0.5;
  const ay = ty(a) + 0.5;
  const bx = tx(b) + 0.5;
  const by = ty(b) + 0.5;
  const dx = bx - ax;
  const dy = by - ay;
  const steps = Math.ceil(Math.sqrt(dx * dx + dy * dy) * 4);
  for (let s = 1; s <= steps; s++) {
    const t = s / steps;
    if (blocksFeet(map, Math.floor(ax + dx * t), Math.floor(ay + dy * t))) return false;
  }
  return true;
}

/** Make one order legal for THIS map from THIS tile, by truncation. */
export function sanitize(map: GameMap, from: number, o: UnitOrder): UnitOrder {
  const path: number[] = [];
  let at = from;
  for (const raw of o.path.slice(0, MAX_WAYPOINTS)) {
    const wp = raw | 0;
    if (wp < 0 || wp >= N) break;
    if (blocksFeet(map, tx(wp), ty(wp))) break;
    if (!walkClear(map, at, wp)) break;
    path.push(wp);
    at = wp;
  }
  return { path, watch: o.watch >= 0 && o.watch < SECTORS ? o.watch | 0 : -1, stance: o.stance };
}

export function sanitizeSide(map: GameMap, starts: number[], orders: SideOrders, units: number): SideOrders {
  const out: SideOrders = [];
  for (let u = 0; u < units; u++) out.push(sanitize(map, starts[u] ?? 0, orders[u] ?? holdOrder()));
  return out;
}

/** FNV-1a over the flat ints plus a nonce — the commitment published before a reveal. */
export function commitHash(flat: number[], nonce: number): number {
  let h = (0x811c9dc5 ^ (nonce >>> 0)) >>> 0;
  for (const v of flat) {
    h ^= v & 0xff;
    h = Math.imul(h, 0x01000193);
    h ^= (v >>> 8) & 0xff;
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/** How far a unit could walk this turn, in tiles — what the ruler on the path ribbon measures. */
export const reachOf = (speed: number, stance: Stance): number =>
  (speed * [50, 100, 160][stance]) / 100 * 5;

export { W };
